import React, { useCallback, useEffect, useState } from 'react';
import { Download, Search, Users } from 'lucide-react';
import api from '../../../utils/api';
import toast from 'react-hot-toast';
import { isoDate } from '../../../utils/time';
import {
  PAGE_WIDE, PageHeader, KpiTile, SectionCard, EmptyState, ErrorState, TableSkeleton, money, num, plural,
} from '../../../components/ui';
import {
  TABLE_WRAP, TABLE, THEAD_ROW, TH, TH_NUM, TR, TD, TD_NUM, TFOOT_ROW, MoneyCell, Notice,
  DateRange, RefreshButton,
} from '../financeKit';

function downloadCSV(filename, headers, rows) {
  const lines = [headers.join(','), ...rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// A labelled search box that lines up with the date fields beside it.
function SearchField({ id, label, value, onChange, placeholder }) {
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-[12px] font-medium text-slate-500">{label}</label>
      <div className="relative">
        <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
        <input id={id} type="search" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          className="input-field !w-48 !py-2 !pl-8 text-sm" />
      </div>
    </div>
  );
}

export default function ContractorReport() {
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [search, setSearch]   = useState('');
  const today = new Date();
  const [from, setFrom] = useState(() => {
    const d = new Date(today.getFullYear(), today.getMonth(), 1);
    return isoDate(d);
  });
  const [to, setTo] = useState(() => isoDate(today));

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/financials/reports/staff-pay?from=${from}&to=${to}`);
      // Only contractors
      setData((r.data || []).filter(d => d.employment_type === 'contractor'));
    } catch (e) {
      setData([]);
      setError(e.response?.data?.message || "We couldn't load contractor payments. Check your connection, then try again.");
    } finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const filtered = search
    ? data.filter(d => d.staff_name?.toLowerCase().includes(search.toLowerCase()) || d.abn?.includes(search))
    : data;

  const totalPay   = filtered.reduce((s, d) => s + parseFloat(d.total_pay || 0), 0);
  const totalShifts = filtered.reduce((s, d) => s + parseInt(d.shifts || 0), 0);
  const totalHours  = filtered.reduce((s, d) => s + parseFloat(d.total_hours || 0), 0);

  // Each row worked out once, so the rows and the totals row read the same figures.
  const rows = filtered.map((row) => {
    const pay      = parseFloat(row.total_pay || 0);
    const shifts   = parseInt(row.shifts || 0);
    const phShifts = parseInt(row.ph_shifts || 0);
    const satShifts = parseInt(row.sat_shifts || 0);
    const sunShifts = parseInt(row.sun_shifts || 0);
    const wkdayShifts = Math.max(shifts - satShifts - sunShifts - phShifts, 0);
    const avgRate  = row.total_hours ? (pay / row.total_hours) : 0;
    return { row, pay, shifts, phShifts, satShifts, sunShifts, wkdayShifts, avgRate };
  });
  const sumWkday = rows.reduce((s, r) => s + r.wkdayShifts, 0);
  const sumSat   = rows.reduce((s, r) => s + r.satShifts, 0);
  const sumSun   = rows.reduce((s, r) => s + r.sunShifts, 0);
  const sumPh    = rows.reduce((s, r) => s + r.phShifts, 0);

  function exportCSV() {
    const headers = ['Contractor Name', 'ABN', 'Shifts', 'Hours', 'Weekday Shifts', 'Sat Shifts', 'Sun Shifts', 'PH Shifts', 'Gross Payment', 'Superannuation', 'Avg/Shift', 'Avg Rate/Hr'];
    const rows = filtered.map(row => {
      const pay = parseFloat(row.total_pay || 0);
      return [
        row.staff_name,
        row.abn || '',
        row.shifts,
        Number(row.total_hours || 0).toFixed(1),
        parseInt(row.shifts || 0) - parseInt(row.sat_shifts || 0) - parseInt(row.sun_shifts || 0) - parseInt(row.ph_shifts || 0),
        row.sat_shifts || 0,
        row.sun_shifts || 0,
        row.ph_shifts || 0,
        pay.toFixed(2),
        '0.00',
        (row.shifts ? pay / row.shifts : 0).toFixed(2),
        (row.total_hours ? pay / row.total_hours : 0).toFixed(2),
      ];
    });
    downloadCSV(`contractor-report-${from}-to-${to}.csv`, headers, rows);
    toast.success(`Exported ${plural(filtered.length, 'contractor')}`);
  }

  return (
    <div className={PAGE_WIDE}>
      <PageHeader
        title="Contractor payments"
        description="Gross payments to contractors from shifts with an approved timesheet. No super and no award multipliers."
        actions={(
          <>
            <SearchField id="contractor-search" label="Search" value={search} onChange={setSearch} placeholder="Name or ABN" />
            <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} idPrefix="contractors" />
            <RefreshButton onClick={load} loading={loading} label="Refresh report" />
            <button type="button" onClick={exportCSV} disabled={filtered.length === 0} className="btn-secondary">
              <Download size={14} /> Export CSV
            </button>
          </>
        )}
      />

      {error ? (
        <div className="card"><ErrorState message={error} onRetry={load} /></div>
      ) : (
        <>
          <Notice tone="slate" title="Contractor payments are shown gross, before tax.">
            No super applies. Contractors look after their own tax (GST and income tax). Rates are the weekday, weekend and public holiday amounts entered for each contractor, with no award multipliers.
          </Notice>

          {/* Summary — equals the totals row below. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiTile label="Contractors" value={loading ? '…' : num(filtered.length)} accent="slate" />
            <KpiTile label="Gross payments" value={loading ? '…' : money(totalPay)} accent="primary"
              sub={loading ? null : 'Before tax, no super'} />
            <KpiTile label="Shifts" value={loading ? '…' : num(totalShifts)} accent="slate" />
            <KpiTile label="Hours" value={loading ? '…' : num(totalHours, 1)} accent="slate" />
          </div>

          <SectionCard title={`Contractors${loading ? '' : ` · ${filtered.length}`}`} icon={Users}>
            {loading ? (
              <TableSkeleton rows={6} cols={8} />
            ) : filtered.length === 0 ? (
              data.length === 0 ? (
                <EmptyState icon={Users} title="No contractors paid in this range"
                  hint="Contractors appear here once their shifts have an approved timesheet. To add one, set a staff member's employment type to Contractor." />
              ) : (
                <EmptyState icon={Search} title="No contractors match your search"
                  hint="Check the name or ABN, or clear the search."
                  action={<button type="button" onClick={() => setSearch('')} className="btn-secondary">Clear search</button>} />
              )
            ) : (
              <div className={TABLE_WRAP}>
                <table className={TABLE}>
                  <thead>
                    <tr className={THEAD_ROW}>
                      <th scope="col" className={TH}>Contractor</th>
                      <th scope="col" className={TH}>ABN</th>
                      <th scope="col" className={TH_NUM}>Shifts</th>
                      <th scope="col" className={TH_NUM}>Hours</th>
                      <th scope="col" className={TH_NUM}>Weekday</th>
                      <th scope="col" className={TH_NUM}>Saturday</th>
                      <th scope="col" className={TH_NUM}>Sunday</th>
                      <th scope="col" className={TH_NUM}>Public holiday</th>
                      <th scope="col" className={TH_NUM}>Super</th>
                      <th scope="col" className={TH_NUM}>Gross payment</th>
                      <th scope="col" className={TH_NUM}>Avg per hour</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(({ row, pay, shifts, phShifts, satShifts, sunShifts, wkdayShifts, avgRate }, i) => (
                      <tr key={row.id || i} className={TR}>
                        <td className={`${TD} font-medium text-slate-800`}>{row.staff_name}</td>
                        <td className={`${TD} whitespace-nowrap font-mono text-[12px]`}>
                          {row.abn || <span className="font-sans text-amber-700">Not set</span>}
                        </td>
                        <td className={TD_NUM}>{num(shifts)}</td>
                        <td className={TD_NUM}>{num(row.total_hours, 1)}</td>
                        <td className={TD_NUM}>{num(wkdayShifts)}</td>
                        <td className={TD_NUM}>{num(satShifts)}</td>
                        <td className={TD_NUM}>{num(sunShifts)}</td>
                        <td className={TD_NUM}>{num(phShifts)}</td>
                        <td className={TD_NUM}><span className="text-slate-500" title="Contractors are not paid super">N/A</span></td>
                        <td className={TD_NUM}><MoneyCell value={pay} strong /></td>
                        <td className={TD_NUM}><MoneyCell value={avgRate} /></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className={TFOOT_ROW}>
                      <td className={TD} colSpan={2}>Total ({plural(filtered.length, 'contractor')})</td>
                      <td className={TD_NUM}>{num(totalShifts)}</td>
                      <td className={TD_NUM}>{num(totalHours, 1)}</td>
                      <td className={TD_NUM}>{num(sumWkday)}</td>
                      <td className={TD_NUM}>{num(sumSat)}</td>
                      <td className={TD_NUM}>{num(sumSun)}</td>
                      <td className={TD_NUM}>{num(sumPh)}</td>
                      <td className={TD_NUM}><span className="font-normal text-slate-500">N/A</span></td>
                      <td className={TD_NUM}><MoneyCell value={totalPay} strong /></td>
                      <td className={TD_NUM}><MoneyCell value={totalHours ? totalPay / totalHours : 0} strong /></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </SectionCard>
        </>
      )}
    </div>
  );
}
